// =============================================
// FORMULARIO DE CUENTA BANCARIA
// Crear y editar cuentas bancarias
// =============================================

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    Box,
    CircularProgress,
    Alert,
} from '@mui/material';
import bankAccountService from '../../services/bankAccountService';

const BankAccountForm = ({ open, onClose, account, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [banks, setBanks] = useState([]);
    const [loadingBanks, setLoadingBanks] = useState(true);
    const [formData, setFormData] = useState({
        bank_id: '',
        account_number: '',
        account_type: 'Corriente',
        account_name: '',
        description: '',
        email: '',
        year: new Date().getFullYear(),
        is_active: true,
    });

    // Cargar bancos al montar
    useEffect(() => {
        loadBanks();
    }, []);

    // Cargar datos de cuenta cuando se edita
    useEffect(() => {
        if (account) {
            setFormData({
                bank_id: account.bank_id || '',
                account_number: account.account_number || '',
                account_type: account.account_type || 'Corriente',
                account_name: account.account_name || '',
                description: account.description || '',
                email: account.email || '',
                year: account.year || new Date().getFullYear(),
                is_active: account.is_active !== undefined ? account.is_active : true,
            });
        } else {
            setFormData({
                bank_id: '',
                account_number: '',
                account_type: 'Corriente',
                account_name: '',
                description: '',
                email: '',
                year: new Date().getFullYear(),
                is_active: true,
            });
        }
    }, [account, open]);

    const loadBanks = async () => {
        try {
            setLoadingBanks(true);
            const response = await bankAccountService.getBanks();
            setBanks(response.banks || []);
        } catch (error) {
            console.error('Error al cargar bancos:', error);
        } finally {
            setLoadingBanks(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setLoading(true);

            if (account) {
                await bankAccountService.update(account.id, formData);
            } else {
                await bankAccountService.create(formData);
            }

            onSuccess();
            onClose();
        } catch (error) {
            alert(error.response?.data?.message || 'Error al guardar cuenta');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>
                    {account ? 'Editar Cuenta Bancaria' : 'Nueva Cuenta Bancaria'}
                </DialogTitle>

                <DialogContent>
                    {loadingBanks ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : banks.length === 0 ? (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            No hay bancos disponibles. Por favor, contacte al administrador.
                        </Alert>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                            <FormControl fullWidth required>
                                <InputLabel>Banco</InputLabel>
                                <Select
                                    value={formData.bank_id}
                                    label="Banco"
                                    onChange={(e) => handleChange('bank_id', e.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>Seleccione un banco</em>
                                    </MenuItem>
                                    {banks.map((bank) => (
                                        <MenuItem key={bank.id} value={bank.id}>
                                            {bank.bank_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                label="Nombre de la Cuenta"
                                value={formData.account_name}
                                onChange={(e) => handleChange('account_name', e.target.value)}
                                required
                                fullWidth
                                helperText="Ej: Cuenta Principal, Cuenta Reservas, etc."
                            />

                            <FormControl fullWidth required>
                                <InputLabel>Tipo de Cuenta</InputLabel>
                                <Select
                                    value={formData.account_type}
                                    label="Tipo de Cuenta"
                                    onChange={(e) => handleChange('account_type', e.target.value)}
                                >
                                    <MenuItem value="Corriente">Corriente</MenuItem>
                                    <MenuItem value="Ahorro">Ahorro</MenuItem>
                                </Select>
                            </FormControl>

                            <TextField
                                label="Número de Cuenta"
                                value={formData.account_number}
                                onChange={(e) => handleChange('account_number', e.target.value)}
                                required
                                fullWidth
                            />

                            <TextField
                                label="Descripción"
                                value={formData.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                                multiline
                                rows={3}
                                fullWidth
                                helperText="Información adicional sobre esta cuenta"
                            />

                            <TextField
                                label="Email de Contacto"
                                type="email"
                                value={formData.email}
                                onChange={(e) => handleChange('email', e.target.value)}
                                fullWidth
                            />

                            <TextField
                                label="Año"
                                type="number"
                                value={formData.year}
                                onChange={(e) => handleChange('year', parseInt(e.target.value))}
                                required
                                fullWidth
                                inputProps={{ min: 2000, max: new Date().getFullYear() + 1 }}
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.is_active}
                                        onChange={(e) => handleChange('is_active', e.target.checked)}
                                    />
                                }
                                label="Cuenta Activa"
                            />
                        </Box>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose} disabled={loading}>
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || loadingBanks || banks.length === 0}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Guardar'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default BankAccountForm;
